import { orderBy, uniq } from 'lodash-es'
import axios, { type AxiosInstance } from 'axios'

import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { CanvasRetrieve } from '@openapi'
import type { PaginatedData } from '@lib/types/paginatedData'
import { urlSafeFetchInChunks } from '@lib/util/network'
import { indirectEval } from '@lib/util/eval'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'

async function resolveToSectionIds(
  searchTerms: string[],
  pitcherSettings?: OrganizationSettings & InstanceSettings,
  restApiAxios?: AxiosInstance,
): Promise<string[] | null> {
  try {
    const resolverFnStr = pitcherSettings?.business_logic_ids_to_section_ids_resolver
    if (!resolverFnStr) {
      return null
    }

    let resolverFn: (axios: any, businessLogicIds: string[], options?: Record<string, any>) => Promise<string[]>

    try {
      resolverFn = resolverFnStr ? indirectEval(`(${resolverFnStr})`) : null
    } catch (evalError) {
      console.error('Eval Error:', evalError)
      return null
    }

    return await resolverFn(axios, searchTerms, { axiosWithAuth: restApiAxios })
  } catch (e) {
    console.error('Error in resolveToSectionIds:', e)
    return null
  }
}

export async function fetchSectionsWithPipeMultiSearch({
  listCanvases,
  search,
  isPipeSearching,
  pipeSearchTerms,
  pageSize,
  currentPage,
  filters,
  sectionIds,
  pitcherSettings,
  searchIn,
  restApiAxios,
}: {
  listCanvases?: (params: any) => Promise<PaginatedData<CanvasRetrieve>>
  search: string
  isPipeSearching: boolean
  pipeSearchTerms?: string[]
  pageSize: number
  currentPage: number
  filters: Record<string, any>
  sectionIds: string[] | null
  pitcherSettings?: OrganizationSettings & InstanceSettings
  searchIn?: string
  restApiAxios?: AxiosInstance
}): Promise<{
  availableSections: CanvasRetrieve[]
  availableSectionsTotalCount: number
  firstPageTotalDisplayedSections: number
}> {
  if (!listCanvases) {
    throw new Error('listCanvases function is not provided')
  }

  let resolvedSectionIds: string[] | null = null

  if (isPipeSearching) {
    resolvedSectionIds = await resolveToSectionIds(pipeSearchTerms ?? [], pitcherSettings, restApiAxios)
  }

  if (!isPipeSearching || resolvedSectionIds === null) {
    if (sectionIds && sectionIds.length > 0) {
      const uniqueSections = await urlSafeFetchInChunks(
        sectionIds,
        async (chunk) =>
          await listCanvases({
            is_section: true,
            is_template: false,
            is_draft: false,
            page_size: chunk.length,
            search,
            filters: normalizeFilterParams(filters),
            id__in: chunk.join(','),
            search_in: searchIn,
          }),
      )

      return {
        availableSections: uniqueSections,
        availableSectionsTotalCount: uniqueSections.length,
        firstPageTotalDisplayedSections: uniqueSections.length,
      }
    } else {
      const response = await listCanvases({
        is_section: true,
        is_template: false,
        is_draft: false,
        page_size: pageSize,
        search,
        page: currentPage,
        filters: normalizeFilterParams(filters),
        id__in: sectionIds ? sectionIds.join(',') : undefined,
        search_in: searchIn,
      })

      return {
        availableSections: response.results,
        availableSectionsTotalCount: response.count,
        firstPageTotalDisplayedSections: response.results.length,
      }
    }
  } else {
    const allSectionIds = uniq(sectionIds ? resolvedSectionIds.concat(sectionIds) : resolvedSectionIds)
    const uniqueSections = await urlSafeFetchInChunks(allSectionIds, async (chunk) => {
      const response = await listCanvases({
        is_section: true,
        is_template: false,
        is_draft: false,
        page_size: pageSize,
        page: currentPage,
        filters: normalizeFilterParams(filters),
        id__in: chunk.join(','),
        search_in: searchIn,
      })

      // NOTE: eventually orderBy could be removed if BE returns id__in order when no other ordering is specified
      return orderBy(response.results, [(section) => allSectionIds.indexOf(section.id)], ['asc'])
    })

    return {
      availableSections: uniqueSections,
      availableSectionsTotalCount: uniqueSections.length,
      firstPageTotalDisplayedSections: uniqueSections.length,
    }
  }
}
